import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useProducts } from '../context/ProductContext';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import '../styles/ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { products, siteSettings, loading } = useProducts();

  const product = products.find(
    (p) => String(p.id) === String(id) || p.slug === id || (p.slug && p.slug.current === id)
  );

  const [selectedType, setSelectedType] = useState('');
  const [selectedImage, setSelectedImage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [customNote, setCustomNote] = useState('');
  const [addedSuccess, setAddedSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (product) {
      const types = product.types || [];
      const initialType = types.length > 0 ? types[0].name : '';
      setSelectedType(initialType);

      // Determine initial image:
      // If the first type has an image, show it; otherwise fall back to cover/product.image
      const firstTypeObj = types.find((t) => t.name === initialType);
      const initialImg =
        firstTypeObj && firstTypeObj.image && firstTypeObj.image !== 'N/A'
          ? firstTypeObj.image
          : (product.gallery && product.gallery.length > 0 ? product.gallery[0] : product.image);

      setSelectedImage(initialImg || product.image);
      setQuantity(1);
      setCustomNote('');
    }
  }, [id, product]);

  if (loading && products.length === 0) {
    return (
      <div className="product-detail-container">
        <div className="product-loading">Loading product details...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-container">
        <div className="product-not-found">
          <h2>Product Not Found</h2>
          <p>We couldn't find the product you're looking for.</p>
          <Link to="/shop" className="button not-found-btn">Back to Shop</Link>
        </div>
      </div>
    );
  }

  // Type change handler: dynamically switches stage image to that type's photo, or falls back to main image
  const handleTypeSelect = (typeName) => {
    setSelectedType(typeName);
    const matchedType = (product.types || []).find((t) => t.name === typeName);
    if (matchedType && matchedType.image && matchedType.image !== 'N/A') {
      setSelectedImage(matchedType.image);
    } else {
      setSelectedImage(product.image);
    }
  };

  const handleThumbnailClick = (img) => {
    setSelectedImage(img);
    if (product.types) {
      const match = product.types.find((t) => t.image === img);
      if (match) {
        setSelectedType(match.name);
      }
    }
  };

  const handleAddToCart = () => {
    if (!product.inStock) return;

    const selectedOptions = {};
    if (selectedType) {
      selectedOptions.type = selectedType;
    }
    if (customNote.trim()) {
      selectedOptions.customNote = customNote.trim();
    }

    // Pass chosen variation ID if this type has one configured in Square
    const matchedType = (product.types || []).find((t) => t.name === selectedType);
    const chosenVariationId = matchedType?.squareVariationId || product.squareVariationId || null;

    addToCart({ ...product, squareVariationId: chosenVariationId }, selectedOptions, quantity);
    setAddedSuccess(true);
    setTimeout(() => {
      setAddedSuccess(false);
    }, 1500);
  };

  // Build clean unique list of gallery images
  const rawGallery = [
    product.image,
    ...(product.gallery || []),
    ...((product.types || []).map((t) => t.image).filter(Boolean)),
  ].filter((img) => img && img !== 'N/A');

  const galleryImages = Array.from(new Set(rawGallery));
  const currentImgIndex = galleryImages.indexOf(selectedImage);

  const getImageLabel = (imgUrl) => {
    if (product.types) {
      const match = product.types.find((t) => t.image === imgUrl);
      if (match) return match.name;
    }
    if (product.galleryDetails) {
      const match = product.galleryDetails.find((item) => item.url === imgUrl);
      if (match) return match.label;
    }
    return product.name;
  };

  const handlePrevImage = () => {
    const nextIdx = currentImgIndex <= 0 ? galleryImages.length - 1 : currentImgIndex - 1;
    const nextImg = galleryImages[nextIdx];
    handleThumbnailClick(nextImg);
  };

  const handleNextImage = () => {
    const nextIdx = currentImgIndex >= galleryImages.length - 1 ? 0 : currentImgIndex + 1;
    const nextImg = galleryImages[nextIdx];
    handleThumbnailClick(nextImg);
  };

  // Dynamic Related Products from loaded catalog
  const relatedProducts = products
    .filter((p) => p.id !== product.id && (p.category === product.category || !product.category))
    .slice(0, 4);

  const productPriceNum = typeof product.priceNum === 'number'
    ? product.priceNum
    : parseFloat(String(product.price || '').replace(/[^0-9.]+/g, '')) || 0;

  const productJsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    image: product.image?.startsWith('http')
      ? product.image
      : `https://starryhwa.com${product.image}`,
    description: product.description || `Handcrafted ${product.name} at Star.Ryhwa.`,
    brand: {
      '@type': 'Brand',
      name: 'Star.Ryhwa',
    },
    offers: {
      '@type': 'Offer',
      url: `https://starryhwa.com/product/${product.id}`,
      priceCurrency: 'USD',
      price: productPriceNum,
      priceValidUntil: '2027-12-31',
      availability: product.inStock !== false ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name: 'Star.Ryhwa',
      },
    },
  };

  return (
    <div className="product-detail-container">
      <SEO
        title={product.name}
        description={product.description || `Shop ${product.name} at Star.Ryhwa. Handcrafted K-Pop goods with fast shipping.`}
        canonicalUrl={`/product/${product.id}`}
        image={product.image}
        type="product"
        jsonLd={productJsonLd}
      />
      {/* Top Breadcrumb Navigation */}
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span className="breadcrumb-separator">&gt;</span>
        <Link to="/shop">Shop</Link>
        {product.category && (
          <>
            <span className="breadcrumb-separator">&gt;</span>
            <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
              {product.category}
            </Link>
          </>
        )}
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">{product.name}</span>
      </nav>

      {/* Main Product Showcase Grid */}
      <div className="product-main-grid">
        {/* Left Column: Media Showcase */}
        <div className="gallery-section">
          {galleryImages.length > 1 && (
            <div className="vertical-thumbnails">
              {galleryImages.map((img, idx) => {
                const label = getImageLabel(img);
                return (
                  <button
                    key={idx}
                    type="button"
                    className={`thumb-btn ${selectedImage === img ? 'active' : ''}`}
                    onClick={() => handleThumbnailClick(img)}
                    title={label}
                  >
                    <img src={img} alt={label} />
                    <span className="thumb-hover-tooltip">{label}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="main-stage" title={getImageLabel(selectedImage)}>
            <img
              src={selectedImage || product.image}
              alt={getImageLabel(selectedImage)}
              className="main-display-img"
            />
            {galleryImages.length > 1 && (
              <div className="image-hover-caption">{getImageLabel(selectedImage)}</div>
            )}
            {galleryImages.length > 1 && (
              <>
                <button
                  type="button"
                  className="nav-arrow prev-arrow"
                  onClick={handlePrevImage}
                  aria-label="Previous photo"
                >
                  &lsaquo;
                </button>
                <button
                  type="button"
                  className="nav-arrow next-arrow"
                  onClick={handleNextImage}
                  aria-label="Next photo"
                >
                  &rsaquo;
                </button>
              </>
            )}
          </div>
        </div>

        {/* Right Column: Product Info & Actions */}
        <div className="details-section">
          {/* Status & Category Badges */}
          <div className="product-badges-row">
            {product.category && (
              <span className="badge category-badge">{product.category}</span>
            )}
            <span className={`badge stock-badge ${product.inStock ? 'in-stock' : 'sold-out'}`}>
              {product.inStock ? 'In Stock' : 'Sold Out'}
            </span>
            {product.itemType === 'custom' && (
              <span className="badge custom-badge">Custom Made</span>
            )}
            {Array.isArray(product.tags) &&
              product.tags.map((tag, i) => (
                <span key={i} className="badge tag-badge">
                  {tag}
                </span>
              ))}
          </div>

          <h1 className="product-title">{product.name}</h1>
          <div className="product-price">{product.price}</div>

          {/* Description from Sanity */}
          {product.description && (
            <div className="product-description-box">
              <p className="product-description-text">{product.description}</p>
            </div>
          )}

          {/* Dynamic Item Types / Variations Selector (from Sanity types) */}
          {product.types && product.types.length > 0 && (
            <div className="product-option-group">
              <label className="option-label">
                Option / Variation: <span className="selected-option-value">{selectedType}</span>
              </label>
              <div className="variation-buttons-grid">
                {product.types.map((typeObj, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`variation-btn ${selectedType === typeObj.name ? 'active' : ''}`}
                    onClick={() => handleTypeSelect(typeObj.name)}
                    disabled={typeObj.inStock === false}
                  >
                    <span>{typeObj.name}</span>
                    {typeObj.inStock === false && (
                      <span className="variation-sold-out">(Sold Out)</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom Order Note (if isCustomizable) */}
          {product.isCustomizable && (
            <div className="product-option-group">
              <label className="option-label" htmlFor="customNote">
                Customization / Bias Instructions:
              </label>
              <textarea
                id="customNote"
                className="custom-textarea"
                rows={3}
                placeholder="Enter member, group, colors, or special request..."
                value={customNote}
                onChange={(e) => setCustomNote(e.target.value)}
              />
            </div>
          )}

          {/* Quantity & Add to Cart Purchase Action */}
          <div className="purchase-actions-row">
            <div className="quantity-control">
              <button
                type="button"
                className="qty-btn"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                disabled={quantity <= 1 || !product.inStock}
                aria-label="Decrease quantity"
              >
                −
              </button>
              <span className="qty-value">{quantity}</span>
              <button
                type="button"
                className="qty-btn"
                onClick={() => setQuantity((q) => q + 1)}
                disabled={!product.inStock}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>

            <button
              type="button"
              className={`add-to-cart-btn ${addedSuccess ? 'success' : ''}`}
              onClick={handleAddToCart}
              disabled={!product.inStock}
            >
              {!product.inStock
                ? 'Sold Out'
                : addedSuccess
                ? '✓ Added to Cart!'
                : 'Add to Cart'}
            </button>
          </div>

          {/* Dynamic Processing Note from Sanity Site Settings */}
          {siteSettings?.processingTime && (
            <div className="dynamic-processing-note">
              <span className="note-sparkle">✨</span>
              <span>
                Estimated processing: <strong>{siteSettings.processingTime}</strong>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Dynamic Related Products */}
      {relatedProducts.length > 0 && (
        <section className="related-products-section">
          <h2 className="related-title">You May Also Like</h2>
          <div className="related-grid">
            {relatedProducts.map((rel) => (
              <div
                key={rel.id}
                className="related-card"
                onClick={() => navigate(`/product/${rel.slug || rel.id}`)}
              >
                <div className="related-img-box">
                  <img src={rel.image} alt={rel.name} loading="lazy" />
                </div>
                <h4>{rel.name}</h4>
                <p className="related-price">{rel.price}</p>
                <button type="button" className="button view-btn">
                  View Item
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
